import { TranslationKey, useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useLocation } from 'react-router';
import { S_Tab, TabsSingleItemState } from '../../../../design/tabs/tab/S_Tab';
import { S_TabsContainer } from '../../../../design/tabs/tabsContainer/S_TabsContainer';
import { IJobDetailsStore } from '../../jobDetails.store';
import { INFERENCE_TYPE, JOB_TYPE, TRAINING_TYPE, TRAINING_VARIANT } from '../../jobs.model';
import { getJobRoutePrefix } from '../../utils';

interface IJobTabsProps {
  jobId?: string;
  jobType: JOB_TYPE;
  validationErrors: IJobDetailsStore['validationErrors'];
  workspaceId: string;
  type: TRAINING_TYPE | INFERENCE_TYPE;
  variant: TRAINING_VARIANT | null;
}

export const JobTabs = observer((props: IJobTabsProps): ReactElement => {
  const { t } = useTranslator();
  const location = useLocation();
  const { jobId, type, jobType, workspaceId, validationErrors, variant } = props;
  function isValidatedTab(key: string): key is Exclude<keyof IJobDetailsStore['validationErrors'], 'showAll'> {
    return validationErrors[key as keyof IJobTabsProps['validationErrors']] !== undefined;
  }

  const getClass = (link: string): TabsSingleItemState => {
    const pathname = location.pathname.split('/');
    const tabName = pathname[pathname.length - 1];
    const className: TabsSingleItemState = tabName === link ? 'active' : 'passive';
    const key = link.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    if (isValidatedTab(key)) {
      const errors = validationErrors[key];
      let isError = false;

      if (typeof errors === 'undefined') {
        return className;
      }

      if (typeof errors === 'boolean') {
        isError = errors;
      } else {
        // Nothing validated yet
        if (errors.size === 0 && !validationErrors.showAll) {
          return className;
        }

        if (key === 'settings' && !variant) {
          isError = true;
        } else {
          isError = Array.from(errors.values()).some((error) => error !== null);
        }
      }

      const classNameSuffix = isError ? 'error' : 'validated';
      return `${className} ${classNameSuffix}` as TabsSingleItemState;
    }

    return className;
  };

  const buildTab = (link: string, label: TranslationKey, disabled?: boolean, tooltip?: string, callback?: () => void): ReactElement => {
    const routePrefix = getJobRoutePrefix(jobType);
    const route =
      jobId === undefined
        ? routePrefix.Create.Paths.withParams({
            workspaceId,
            tab: link,
          })
        : routePrefix.Details.Paths.withParams({
            workspaceId,
            jobId: jobId,
            tab: link,
          });

    return (
      <S_Tab
        text={t(label)}
        link={route}
        state={getClass(link)}
        callback={callback}
        disabled={disabled}
        tooltip={tooltip}
      />
    );
  };

  if (jobType === JOB_TYPE.UNKNOWN) {
    return <></>;
  }

  return (
    <S_TabsContainer>
      {jobType !== JOB_TYPE.INFERENCE && buildTab('settings', 'settings')}
      {jobType !== JOB_TYPE.INFERENCE && buildTab('annotation-tools', 'annotation_tools')}
      {buildTab('datasets', 'datasets')}
      {buildTab('images', 'images')}
      {jobId !== undefined ? buildTab('metrics', 'metrics.title') : <></>}
      {jobId !== undefined && type === TRAINING_TYPE.AUTO ? buildTab('training-jobs', 'training_jobs') : <></>}
    </S_TabsContainer>
  );
});
